/**
 * @hon/schema — the content-pack contract.
 *
 * This is the single source of truth shared by the game runtime, the Workers API,
 * and (later) the third-party asset editor. If a pack validates against these
 * schemas, every surface can load it. Change this file deliberately.
 */
import { z } from 'zod';

/* ── engine version ─────────────────────────────────────────────────────── */

/**
 * The pack-format version this runtime implements. A pack declares what it
 * needs in `PackManifest.engine`; the loader refuses anything newer.
 *
 * **Pre-1.0, the MINOR is the breaking axis** (semver §4: "anything MAY change
 * at any time" while major is 0). `seam` and `peakChroma` widened the format in
 * 0.2.0, and a 0.1.x runtime hard-fails on a pack using them — so a runtime
 * that compared majors only would load such a pack and crash mid-scene instead
 * of asking the player to update. Bump this on every new opcode, enum member,
 * or required field.
 */
export const ENGINE_VERSION = '0.4.0';

/* ── primitives ─────────────────────────────────────────────────────────── */

export const AssetRef = z.string().min(1).describe('Pack-relative asset path, e.g. "bg/classroom.webp"');

export const Position = z.enum(['left', 'center', 'right', 'far-left', 'far-right']);

export const Transition = z.object({
  /**
   * `seam` (0.2.0) is the book's signature wipe: a hard vertical edge sweeping
   * left→right with a blown-white seam column. It is enum *widening*, so a pack
   * using it MUST declare `engine: "^0.2.0"` — an older runtime rejects the
   * whole pack, which is why the loader's version gate has to work first.
   */
  kind: z.enum(['none', 'fade', 'dissolve', 'slide', 'glitch', 'pixelate', 'seam']).default('fade'),
  ms: z.number().int().min(0).max(10_000).default(300),
});

/**
 * Expression evaluated against the story flags set by `set`.
 *
 * Flags are addressable bare (`trust`) or namespaced (`vars.trust`) — both reach
 * the same value. There is no `flags.` or `affection.` namespace; a previous
 * version of this docstring claimed otherwise and the example could never be true.
 *
 * Ordering comparisons (`>` `>=` `<` `<=`) require numbers on both sides.
 * Equality (`==` `!=`) is strict: `1 == "1"` is false. Chained comparisons
 * (`a > b > c`) are rejected — use `&&`.
 *
 * e.g. `trust > 0 && route == "marina" && !ending_seen`
 */
export const Condition = z.string().min(1);

/* ── opcodes ────────────────────────────────────────────────────────────── */

const Say = z.object({
  op: z.literal('say'),
  who: z.string().optional().describe('Character id; omit for narration'),
  text: z.string(),
  voice: AssetRef.optional(),
  emote: z.string().optional().describe('Portrait expression id'),
  /**
   * Whether the character says this aloud or thinks it (0.3.0).
   *
   * The script marks the difference on nearly every one of the heroine's lines —
   * «Марина – в кадре» against «Марина – в кадре (мысль)» — and a first-person
   * narrator whose thoughts read identically to her speech loses the distinction
   * the whole voice is built on. Additive and optional: a runtime that does not
   * know the field renders every line as speech, which is what it did before.
   */
  tone: z.enum(['speech', 'thought']).optional(),
  /**
   * Which of four heights the dialogue frame sits at for this line.
   *
   * Marina moves the frame to keep it off whoever is talking, and the boards
   * cluster hard into four positions — y39% for a wide shot, y46%, y55%, and
   * y62% for a close-up. What they do NOT do is agree on a size: the same frame
   * is drawn anywhere from 16% to 27% tall depending on how the rectangle was
   * dragged that day.
   *
   * Transcribing the rectangle reproduced that jitter faithfully and the box
   * changed shape on almost every line. A slot keeps the placement, which is
   * deliberate, and drops the size, which is not — one frame, at its own
   * aspect, at one of four heights.
   */
  at: z.enum(['upper', 'middle', 'lower', 'bottom']).optional(),
});



/**
 * Where a sprite stands, as a rectangle on the 402x874 board.
 *
 * `at` puts a character in one of five slots at full frame height, which is all
 * an author writing prose can reasonably say. A storyboard says far more: the
 * art is drawn 122% or 178% of the frame wide, pushed off one edge, cropped at
 * a particular height. `frame` carries that when it is known — percentages,
 * because the board is 402 wide and the game runs at 1080.
 */
const Frame = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
});

const Show = z.object({
  op: z.literal('show'),
  sprite: z.string(),
  at: Position.default('center'),
  emote: z.string().optional(),
  /** Exact placement off the storyboard. Falls back to `at` when absent. */
  frame: Frame.optional(),
  with: Transition.optional(),
});

/**
 * Ask the player for a name (0.4.0).
 *
 * «Выберите имя:» is a screen in the storyboard — frame 7.3, a plate carrying
 * the label, the current name and a pencil, over a warning that Russian
 * declension will not follow a name the player invents. It could not be built
 * until now because nothing in the format could ask a question whose answer is
 * text rather than a branch.
 *
 * It writes a declared variable like any `set` does, so the linter's rules
 * about reading what you write apply to it unchanged.
 */
const Input = z.object({
  op: z.literal('input'),
  /** The variable the answer lands in. Must be declared, and must be a string. */
  key: z.string(),
  /** The label beside the field: «Выберите имя:». */
  prompt: z.string(),
  /** The warning above it, if the screen has one. */
  note: z.string().optional(),
  /** What the field starts as, and what an empty answer falls back to. */
  default: z.string(),
  maxLength: z.number().int().positive().default(24),
});

/**
 * A card the story stops on — «ТРИ ДНЯ НАЗАД», «Настоящее время» (0.4.0).
 *
 * The same construction as the episode's own title card: the scene behind it
 * under a half-black wash, a hairline rule, and one or two lines over it. The
 * storyboard uses it twice to move the episode in time, and without it the
 * flashback to James's workshop begins with no warning that three days have
 * passed — the reader simply finds themselves somewhere else.
 *
 * It holds until the player advances, like a line of dialogue does.
 */
const Card = z.object({
  op: z.literal('card'),
  /** The big line, set in the display face. «ТРИ ДНЯ НАЗАД». */
  title: z.string().optional(),
  /** The line under the rule, set in the story face. «Мастерская Джеймса Харриса». */
  subtitle: z.string().optional(),
});

const Hide = z.object({ op: z.literal('hide'), sprite: z.string(), with: Transition.optional() });

/**
 * Change the scene behind everything (0.4.0 adds `frame`).
 *
 * The background is not a picture that fills the screen — it is a picture the
 * camera looks at part of. Marina places one 1448x1086 photograph at 290% of
 * the board and pans it: x-114% for most of the episode, x-10% for the hall and
 * the staircase, x-157% and x-167% for the far end of the kitchen. Six framings
 * of one image, and rendered centred every time they collapse into one room —
 * which is why beats set somewhere else looked like the wrong background.
 */
const Bg = z.object({
  op: z.literal('bg'),
  image: AssetRef,
  /** Where the camera sits on the image, as a rectangle on the board. */
  frame: Frame.optional(),
  with: Transition.optional(),
});
const Bgm = z.object({ op: z.literal('bgm'), track: AssetRef.nullable(), fadeMs: z.number().int().min(0).default(800) });
const Sfx = z.object({ op: z.literal('sfx'), clip: AssetRef, volume: z.number().min(0).max(1).default(1) });
const Fx = z.object({
  op: z.literal('fx'),
  effect: z.enum(['shake', 'flash', 'rain', 'bloom', 'chromatic', 'vignette']),
  ms: z.number().int().min(0).default(500),
  intensity: z.number().min(0).max(1).default(0.5),
});
const Wait = z.object({ op: z.literal('wait'), ms: z.number().int().min(0).max(30_000) });
// Named SetOp, not Set: a module-scoped `const Set` shadows the global Set
// constructor for the whole file, so `new Set()` further down stops compiling.
const SetOp = z.object({ op: z.literal('set'), key: z.string().min(1), value: z.union([z.string(), z.number(), z.boolean()]) });
const Jump = z.object({ op: z.literal('jump'), scene: z.string().min(1) });

/**
 * Move a numeric variable by a relative amount (0.3.0).
 *
 * The condition language has no arithmetic on purpose, and the original plan was
 * for scalars to be recomputed from booleans at each episode exit. That holds for
 * a handful of 0-6 counters. It does not hold for this book: the narrative bible
 * runs three path stats and two character stats on 0-100, accumulating across a
 * whole season, and every choice contributes. Recomputing those from booleans
 * means an if-ladder over every combination that has ever mattered, which stops
 * being maintainable around episode three.
 *
 * The clamp is not optional and not the author's job: `by` is applied and then
 * pinned inside the variable's declared domain, so a stat cannot be walked out
 * of range by content and no `if` downstream has to defend against it.
 */
const Adjust = z.object({
  op: z.literal('adjust'),
  key: z.string().min(1),
  by: z.number().int().describe('Signed delta, e.g. 1 or -2'),
});

/**
 * Economy fields are declared but NOT enforced — everything is free today.
 * They exist so content authored now stays valid if a currency model lands later;
 * see `Episode.cost`. The runtime ignores both.
 */
export const Price = z.object({
  diamonds: z.number().int().nonnegative().default(0),
});

const ChoiceOption = z.object({
  text: z.string(),
  goto: z.string().min(1).describe('Target scene id, within the same episode'),
  if: Condition.optional().describe('Hide this option unless the condition holds'),
  cost: Price.optional().describe('Unenforced. Once paid, an unlock is permanent.'),
  premium: z.boolean().default(false).describe('Unenforced. Marks a paid-tier branch.'),
  /**
   * Stable identity for this option, e.g. "open". See `Choice.id`.
   * Unique within its choice. Never regenerate one — that is the whole point.
   */
  key: z.string().min(1).optional(),
});
const Choice = z.object({
  op: z.literal('choice'),
  /**
   * Stable identity for this choice, e.g. "k1_open_drawer". Unique within its episode.
   *
   * Array indices are NOT stable: inserting one option above another silently
   * re-points every record keyed on position. That is survivable for a recap and
   * fatal for a paid-unlock ledger, so `ChoiceRecord.c` stores this instead.
   * Optional in the format; `assertStableChoiceIds` requires it wherever money
   * could ever be involved, and the validator requires it on anything recorded.
   */
  id: z.string().min(1).optional(),
  prompt: z.string().optional(),
  /**
   * Art for the prompt, pack-relative — the object the choice is about.
   *
   * Figma draws two shapes of choice. Most are plates stacked under the line
   * that prompted them. One in episode 1 is a glass card holding the thing
   * itself: a bottle, named «МАДИРАН», with the options below it. The card is
   * the prompt and this is what goes on it. Optional, and the card falls back
   * to the prompt text alone where a pack has not supplied one.
   */
  promptImage: z.string().optional(),
  options: z.array(ChoiceOption).min(1),
});

/** Recursive — `if` nests node arrays, so this needs an explicit type annotation. */
export type Node =
  | z.infer<typeof Say> | z.infer<typeof Show> | z.infer<typeof Hide>
  | z.infer<typeof Bg> | z.infer<typeof Bgm> | z.infer<typeof Sfx>
  | z.infer<typeof Fx> | z.infer<typeof Wait> | z.infer<typeof SetOp>
  | z.infer<typeof Adjust> | z.infer<typeof Jump> | z.infer<typeof Choice>
  | z.infer<typeof Input> | z.infer<typeof Card>
  // `| undefined` is explicit because exactOptionalPropertyTypes is on and zod's
  // .optional() widens to `Node[] | undefined`.
  | { op: 'if'; cond: string; then: Node[]; else?: Node[] | undefined };

// Input is `unknown`, not `Node`: several fields use .default(), so the parsed
// output type is stricter than what a valid pack.json is allowed to contain.
export const Node: z.ZodType<Node, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.discriminatedUnion('op', [
    Say, Show, Hide, Bg, Bgm, Sfx, Fx, Wait, SetOp, Adjust, Jump, Choice, Input, Card,
    z.object({ op: z.literal('if'), cond: Condition, then: z.array(Node), else: z.array(Node).optional() }),
  ]),
);

/* ── content ────────────────────────────────────────────────────────────── */

export const Scene = z.object({
  id: z.string().min(1).describe('Unique within its episode, not globally'),
  label: z.string().optional(),
  nodes: z.array(Node),
});

/**
 * An episode is the unit of play and the unit of release — one sitting, one
 * download, one entry in the player's progress list. Scenes never jump across
 * episode boundaries; finishing an episode's graph ends it and hands control
 * back to the host, which decides what to load next.
 */
export const Episode = z.object({
  id: z.string().min(1).describe('Unique within its book'),
  number: z.number().int().positive(),
  title: z.string().min(1),
  synopsis: z.string().max(1000).optional(),
  entry: z.string().min(1).describe('Scene id this episode starts from'),
  scenes: z.array(Scene).min(1),
  cost: Price.optional().describe('Unenforced today; free to play.'),
  reward: Price.optional().describe('Unenforced. Granted on completion.'),
  /** epoch ms; lets the client hide unreleased episodes without a server call. */
  releasedAt: z.number().int().optional(),
  /**
   * Ceiling on lateral RGB split for this episode, in render px (0–3).
   *
   * The horror escalates across a book, so the chromatic-aberration budget is
   * authored per episode rather than per effect: Ep1 sits low, later episodes
   * open up. Omitted means the absolute 3.0 ceiling. Additive-optional — a 0.1.x
   * runtime ignores it and renders at full strength, which is why this was safe
   * to land before `seam`.
   */
  peakChroma: z.number().min(0).max(3.0).optional(),
});

export const Book = z.object({
  id: z.string().min(1).describe('Unique within its pack'),
  title: z.string().min(1),
  description: z.string().max(2000).optional(),
  cover: AssetRef.optional(),
  /** Art is authored per book and never shared between books. */
  episodes: z.array(Episode).min(1),
});

export const Character = z.object({
  id: z.string().min(1),
  name: z.string(),
  /**
   * A variable that overrides `name` once the player has set one.
   *
   * The heroine is «Марина» until the player renames her, and the speaker plate
   * has to follow. Falls back to `name` while the variable is empty, so a pack
   * that never asks reads exactly as it did before.
   */
  nameVar: z.string().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#ffffff'),
  portraits: z.record(z.string(), AssetRef).describe('emote id → asset path'),
});

/**
 * A variable the book is allowed to touch (0.3.0).
 *
 * This lives in the pack rather than in a build-time-only file because the
 * runtime genuinely needs it. Two things become the engine's job instead of
 * every author's:
 *
 *  - **Clamping.** `adjust` cannot pin a value inside a range it cannot see.
 *  - **Defaulting.** An unset name is `undefined`, and `undefined` compares
 *    false, so a variable added in episode 8 reads false for every player who
 *    passed episode 3 before it existed. Declaring it means the runtime fills it
 *    in on load, which is both more reliable than a defaulting guard at every
 *    episode entry and impossible for an author to forget.
 *
 * Names carry their kind as a prefix: `f_` story flag · `k_` knowledge ·
 * `s_` pressure/path scalar · `r_` relationship scalar · `m_` meta-layer ·
 * `c_` cosmetic. `@hon/validate` enforces the rest.
 */
export const VarDecl = z.object({
  name: z.string().regex(/^[fksrmc]_[a-z0-9_]{1,22}$/, 'prefix + snake_case, 3–24 chars, e.g. "s_iskuplenie"'),
  type: z.enum(['boolean', 'int', 'string']),
  /** Inclusive `[min, max]`. Required for `s_`/`r_`; what `adjust` clamps to. */
  domain: z.tuple([z.number().int(), z.number().int()]).optional(),
  /** Value a player starts with. Defaults to false / the domain floor / "". */
  initial: z.union([z.string(), z.number(), z.boolean()]).optional(),
  note: z.string().max(300).optional(),
});

export type VarDecl = z.infer<typeof VarDecl>;

/** The value a variable holds before anything in the story has touched it. */
export function initialValue(v: VarDecl): string | number | boolean {
  if (v.initial !== undefined) return v.initial;
  if (v.type === 'boolean') return false;
  if (v.type === 'int') return v.domain?.[0] ?? 0;
  return '';
}

/* ── pack manifest ──────────────────────────────────────────────────────── */

const PackManifestShape = z.object({
  /** Reverse-DNS-ish id, e.g. "com.teamhon.base" — unique across the registry. */
  id: z.string().regex(/^[a-z0-9]+(\.[a-z0-9-]+)+$/),
  name: z.string().min(1),
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'semver'),
  author: z.string().min(1),
  /**
   * Which runtime this pack expects, e.g. "^0.2.0". The loader refuses anything
   * newer than `ENGINE_VERSION` — and pre-1.0 that comparison is on the minor,
   * not the major.
   */
  engine: z.string().regex(/^\^?\d+\.\d+\.\d+$/).default('^0.1.0'),
  description: z.string().max(500).optional(),
  locales: z.array(z.string()).default(['en']),
  characters: z.array(Character).default([]),
  /**
   * Every variable this pack may read or write. The runtime initialises these
   * on load and clamps `adjust` against their domains; the validator refuses
   * any name that is not here, which is what turns a typo in a condition from a
   * silently-hidden branch into a build failure.
   */
  vars: z.array(VarDecl).max(60).default([]),
  books: z.array(Book).min(1),
  /** Declared up front so the loader can prefetch and the editor can flag missing files. */
  assets: z.array(z.object({
    path: AssetRef,
    kind: z.enum(['image', 'audio', 'video', 'font', 'shader']),
    bytes: z.number().int().nonnegative().optional(),
    sha256: z.string().length(64).optional(),
  })).default([]),
});

export const PackManifest = PackManifestShape.superRefine(assertStableChoiceIds);

/**
 * Anything that could ever be billed, or recorded against a ledger, must be
 * addressable by a name instead of by an array position.
 *
 * The rule only fires on choices carrying `cost`/`premium`, so it is inert
 * while the economy is off and Book 1 authors nothing extra — but the moment
 * someone marks an option paid, the pack stops validating until that option has
 * a stable key. That is deliberate: an unkeyed paid branch is only detectable
 * as a bug *after* real purchases have been mis-attributed.
 *
 * This lives on the manifest rather than on `Choice`, where the spec put it,
 * because `Choice` is a member of the `Node` discriminated union and zod
 * refuses a refined object as a union member (it reads `.shape` off the member
 * to build the discriminator map, which a ZodEffects wrapper does not have).
 * Walking from the manifest also produces a path a human can act on.
 */
function assertStableChoiceIds(pack: z.infer<typeof PackManifestShape>, ctx: z.RefinementCtx): void {
  pack.books.forEach((book, bi) => {
    book.episodes.forEach((ep, ei) => {
      const idsInEpisode = new Map<string, string>();

      ep.scenes.forEach((scene, si) => {
        const walk = (nodes: Node[], path: (string | number)[]): void => {
          nodes.forEach((node, ni) => {
            const at = [...path, ni];
            if (node.op === 'if') {
              walk(node.then, [...at, 'then']);
              if (node.else) walk(node.else, [...at, 'else']);
              return;
            }
            if (node.op !== 'choice') return;

            const paid = node.options.filter((o) => o.cost || o.premium);
            if (paid.length > 0 && !node.id) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...at, 'id'],
                message: 'Choice.id is required when any option has cost/premium — array indices are not stable across edits',
              });
            }
            for (const [oi, o] of node.options.entries()) {
              if ((o.cost || o.premium) && !o.key) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [...at, 'options', oi, 'key'],
                  message: 'ChoiceOption.key is required when cost/premium is set',
                });
              }
            }

            const keys = new Set<string>();
            for (const [oi, o] of node.options.entries()) {
              if (!o.key) continue;
              if (keys.has(o.key)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [...at, 'options', oi, 'key'],
                  message: `duplicate option key "${o.key}" within this choice`,
                });
              }
              keys.add(o.key);
            }

            if (node.id) {
              const seenAt = idsInEpisode.get(node.id);
              if (seenAt) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [...at, 'id'],
                  message: `duplicate Choice.id "${node.id}" in episode "${ep.id}" (already used in scene "${seenAt}")`,
                });
              }
              idsInEpisode.set(node.id, scene.id);
            }
          });
        };
        walk(scene.nodes, ['books', bi, 'episodes', ei, 'scenes', si, 'nodes']);
      });
    });
  });
}

export type PackManifest = z.infer<typeof PackManifest>;
export type Book = z.infer<typeof Book>;
export type Episode = z.infer<typeof Episode>;
export type Scene = z.infer<typeof Scene>;
export type Character = z.infer<typeof Character>;
export type Transition = z.infer<typeof Transition>;
export type Position = z.infer<typeof Position>;
export type Price = z.infer<typeof Price>;

/** Locate an episode by book + episode id. Returns undefined if either is wrong. */
export function findEpisode(pack: PackManifest, bookId: string, episodeId: string) {
  const book = pack.books.find((b) => b.id === bookId);
  const episode = book?.episodes.find((e) => e.id === episodeId);
  return book && episode ? { book, episode } : undefined;
}

/** The first episode of the first book — where a new player starts. */
export function firstEpisode(pack: PackManifest) {
  const book = pack.books[0];
  const episode = book?.episodes[0];
  if (!book || !episode) throw new Error(`[hon] pack "${pack.id}" has no episodes`);
  return { book, episode };
}

/* ── catalog ────────────────────────────────────────────────────────────── */

/**
 * The store/library listing. Separate from packs on purpose: a book can be
 * announced with cover art and a synopsis long before any playable pack exists,
 * which is exactly the state most of the catalog is in.
 */
const Localized = z.object({ ru: z.string(), en: z.string() });

export const CatalogEntry = z.object({
  id: z.string().min(1),
  /** null until a playable pack exists for this book. */
  packId: z.string().nullable(),
  status: z.enum(['available', 'coming-soon', 'draft']),
  title: Localized,
  genres: z.object({ ru: z.array(z.string()), en: z.array(z.string()) }),
  synopsis: Localized,
  cover: AssetRef.nullable(),
  motion: AssetRef.nullable().describe('Looping video cover for the featured slot'),
});

export const Catalog = z.object({
  version: z.literal(1),
  books: z.array(CatalogEntry),
});

export type Catalog = z.infer<typeof Catalog>;
export type CatalogEntry = z.infer<typeof CatalogEntry>;

/* ── save state ─────────────────────────────────────────────────────────── */

/**
 * A choice the player made, kept for a "choices that mattered" recap.
 *
 * Keys are single letters and the option label is not stored, both on purpose:
 * this array is the only part of a save that grows without bound during play,
 * and it rides in `localStorage` alongside everything else. A record is ~62 B,
 * so a 120-choice season costs ~7.4 KB. Inlining labels would roughly
 * quadruple that for text the pack already holds — recap renders by looking `c`
 * up in the pack, so the label follows whatever the player's build says today.
 *
 * `e` is not redundant with `s`. Scene ids are episode-scoped, so two episodes
 * may both contain a scene called `kitchen`; without the episode id a record
 * cannot be attributed, and `choices[]` deliberately survives across episodes.
 */
export const ChoiceRecord = z.object({
  /** Episode id. */
  e: z.string(),
  /** Scene id, scoped to `e`. */
  s: z.string(),
  /** `Choice.id` when the author set one, else the choice's ordinal within the scene. */
  c: z.string(),
  /** The ORIGINAL option index handed to `choose()`, matching that call's contract. */
  o: z.number().int().nonnegative(),
  /** epoch ms */
  t: z.number().int(),
});

/**
 * Save format, version 3.
 *
 * v2 stored a single `cursor`, but the interpreter runs a frame STACK — `if`
 * pushes a nested frame. Restoring a save made inside a branch therefore applied
 * the branch-local index to the scene body and landed on an unrelated node.
 * v3 stores the cursor at every level instead, and rebuilds the stack by
 * re-evaluating each `if` against the restored flags (deterministic, since flags
 * are restored first).
 *
 * The deferred-system fields (`affection`, `stats`, `look`, `outfit`) are
 * present from the start. They cost nothing empty and mean turning any of those
 * systems on later is additive rather than another migration.
 */
export const SaveState = z.object({
  version: z.literal(3),
  packId: z.string(),
  book: z.string(),
  episode: z.string(),
  scene: z.string(),
  /** Cursor at each frame level, outermost first. Length 1 means the scene body. */
  stack: z.array(z.number().int().nonnegative()).min(1),
  /** Episode ids already finished, so the host can offer the next one. */
  completed: z.array(z.string()).default([]),
  vars: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
  seen: z.array(z.string()).default([]).describe('Node fingerprints, for skip-read'),
  /** Capped so a pathological replay loop cannot grow a save past the storage quota. */
  choices: z.array(ChoiceRecord).max(400).default([]),
  savedAt: z.number().int().describe('epoch ms'),
  playtimeMs: z.number().int().nonnegative().default(0),

  /* Reserved for deferred systems — see docs/design/DECISIONS.md. */
  affection: z.record(z.string(), z.number()).default({}),
  stats: z.record(z.string(), z.number()).default({}),
  look: z.record(z.string(), z.string()).default({}),
  outfit: z.record(z.string(), z.string()).default({}),
});

export type SaveState = z.infer<typeof SaveState>;
export type ChoiceRecord = z.infer<typeof ChoiceRecord>;

/** v2, accepted only as migration input. */
const SaveStateV2 = z.object({
  version: z.literal(2),
  packId: z.string(),
  book: z.string(),
  episode: z.string(),
  scene: z.string(),
  cursor: z.number().int().nonnegative(),
  completed: z.array(z.string()).default([]),
  vars: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
  seen: z.array(z.string()).default([]),
  savedAt: z.number().int(),
  playtimeMs: z.number().int().nonnegative().default(0),
});

/**
 * Load a save of any supported version, upgrading it to the current one.
 *
 * v2 saves become single-frame v3 saves, which is correct: v2 could not
 * represent a nested position in the first place. `seen` is dropped rather than
 * carried, because v3 changed the fingerprint format — a stale entry would make
 * skip-read skip unread lines, so losing skip-read history is the safe failure.
 */
export function loadSave(input: unknown): SaveState {
  const v3 = SaveState.safeParse(input);
  if (v3.success) return v3.data;

  const v2 = SaveStateV2.safeParse(input);
  if (v2.success) {
    const { cursor, seen: _staleFingerprints, version: _v, ...rest } = v2.data;
    return SaveState.parse({ ...rest, version: 3, stack: [cursor], seen: [] });
  }

  /*
   * A v3 save whose only problem is `choices` — the record shape changed inside
   * v3, before any episode had a `releasedAt`, so the only saves affected are on
   * our own machines. Losing a recap beats refusing to load the playthrough;
   * anything else wrong with the save still throws below.
   */
  if (isRecord(input) && input['version'] === 3 && Array.isArray(input['choices'])) {
    const retry = SaveState.safeParse({ ...input, choices: [] });
    if (retry.success) {
      console.warn('[hon] dropped a pre-release choice log this build cannot read');
      return retry.data;
    }
  }

  throw new Error('[hon] unrecognised save format');
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Validate an untrusted pack (third-party upload, editor export, R2 object). */
export function parsePack(input: unknown): PackManifest {
  return PackManifest.parse(input);
}

export function safeParsePack(input: unknown) {
  return PackManifest.safeParse(input);
}
